<template>
  <section class="strength dark" id="strength">
    <div class="container">
      <div class="strength-panel">
        <section-title
          align="left"
          tone="brand"
          data-aos="fade-up"
          :kicker="t('A choice that makes the difference.')"
          :title="t('Why choose us?')"
          :lede="t('Never get deceived by company histories, fake testimonials or free gifts. What matters is how we work, what we charge, who looks after the site.')"
        />
        <div class="strength-cards">
          <article class="strength-card" data-aos="fade-up">
            <div class="strength-visual">
              <span class="strength-pill">{{ t('Lowest price guaranteed') }}</span>
            </div>
            <div class="strength-caption">
              <h3>{{ t('Lowest price on real work') }}</h3>
              <p>{{ t('You pay for the website, not a high-rise studio. Custom work on a plan at the lowest price.') }}</p>
            </div>
          </article>

          <article class="strength-card" data-aos="fade-up">
            <div class="strength-visual strength-visual--art">
              <img src="/images/home/strength/team.png" alt="" />
            </div>
            <div class="strength-caption">
              <h3>{{ t('One team for the whole job') }}</h3>
              <p>{{ t('Design, code, hosting, search and ads sit with us. You do not juggle five vendors for every skill.') }}</p>
            </div>
          </article>

          <article class="strength-card" data-aos="fade-up">
            <div class="strength-visual strength-visual--art strength-visual--extras">
              <img src="/images/home/strength/extras.png" alt="" />
            </div>
            <div class="strength-caption">
              <h3>{{ t('Extras stay on the plan') }}</h3>
              <p>{{ t('Hosting, domain, SSL and email ship with the build. They stay on your plan, not as later add-ons.') }}</p>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';


.strength {
  /* Slight diagonal gradient anchored on $brand-deep, lifting to a lighter blue
     toward the top left so the band is not a flat fill. */
  background: $grad-deep;
}

.strength-panel {
  background: linear-gradient(90deg, $primary, $accent-blue);
  border: 2px solid $on-dark-line-strong;
  border-radius: $r-xl;
  /* A dark navy shadow, because the old blue one sat on white and would now
     disappear against the blue band. */
  box-shadow: $shadow-band;
  overflow: hidden;
  padding: 2.75rem 2.25rem 2.25rem;
  position: relative;

  &::before {
    background-image: url('/images/home/hero/bg.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    bottom: 0;
    content: '';
    left: 0;
    opacity: 0.5;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }

  @media (min-width: 768px) {
    padding: 3.25rem 2.75rem 2.75rem;
  }
}

/* The kicker, title and lede are a SectionTitle now. They used to be an h5, an
   h1 and a p that re-declared the dark lede spec with its own max-width, which
   is why this block measured differently from every other section head.
   The stacking context is the only thing the panel still owes them, so the
   glass layer behind the panel stays behind the copy. */
.strength-panel .section-title,
.strength-cards {
  position: relative;
  z-index: 1;
}

.strength-cards {
  display: grid;
  gap: 1.1rem;
  grid-template-columns: 1fr;

  @media (min-width: 992px) {
    column-gap: 1.1rem;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-template-rows: auto auto;
    row-gap: 0;
  }
}

.strength-card {
  background: transparent;
  border: 4px solid rgba($dark-accent, .42);
  border-radius: $r-lg;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: border-color $dur $ease, box-shadow $dur $ease, transform $dur $ease;

  /* The 4px trim has real presence, so the hover mostly firms that up and lets
     the card come forward. overflow: hidden on the card is what lets the
     artwork inside scale without spilling past the corners. */
  &:hover {
    border-color: rgba($dark-accent, .78);
    box-shadow: $shadow-band;
    transform: translateY(-4px);

    .strength-pill {
      box-shadow: $shadow-lift;
    }

    .strength-visual--art img,
    .strength-visual--extras img {
      transform: scale(1.04);
    }
  }

  @media (min-width: 992px) {
    display: grid;
    grid-row: span 2;
    grid-template-rows: subgrid;
  }
}

.strength-visual {
  align-items: center;
  background: rgba($brand-dark, .64);
  display: flex;
  flex: 1;
  justify-content: center;
  min-height: 11.5rem;
  overflow: hidden;
  padding: 1.5rem 1.25rem;
}

/* The pill is a flex item in this card's centred `.strength-visual`, and it used
   to carry `white-space: nowrap`. That is what stopped it resizing: on a flex
   item the automatic minimum size is its min-content width, and with no break
   allowed min-content is the entire line. So the pill could not shrink at all,
   the row had no way to give it room, and the width it needed at 0.9rem plus its
   padding and 5px trim measured wider than the visual inside a phone. The
   overflow went symmetrically off both ends and `.strength-visual`'s
   `overflow: hidden` clipped each rounded cap.

   Two things put that right. Allowing the break lets the item shrink, because
   min-content becomes the longest word rather than the whole label, and the
   centred `text-align` keeps a wrapped label symmetrical. The step down at phone
   widths then keeps it on a single line wherever there is room, which is almost
   everywhere: the label measures around 190px at 0.78rem against roughly 231px
   available at 375px, so it holds one line down to about a 330px viewport and
   only breaks below that. The desktop step is unchanged, so nothing above 768px
   repaints. */
.strength-pill {
  background: $ground;
  background-clip: padding-box;
  border: 5px solid $on-dark-muted;
  border-radius: $r-pill;
  color: $primary;
  font-family: $headings-font-family;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.6rem 1.05rem;
  text-align: center;
  transition: box-shadow $dur $ease;

  @media (min-width: 768px) {
    font-size: 0.9rem;
    padding: 0.7rem 1.25rem;
  }
}

.strength-visual--art {
  padding: 0.85rem 1rem;

  img {
    display: block;
    height: auto;
    max-height: 9.5rem;
    object-fit: contain;
    transition: transform $dur $ease;
    width: 100%;
  }
}

.strength-visual--extras {
  padding: 0.65rem 0.85rem;

  img {
    margin: 0 auto;
    max-height: 10.25rem;
    transition: transform $dur $ease;
    width: auto;
  }
}

.strength-caption {
  background: rgba($dark-accent, .42);
  display: flex;
  flex-direction: column;
  padding: 1.25rem 1.35rem 1.4rem;

  h3 {
    color: $on-dark-strong;
    font-size: 1.15rem;
    margin-bottom: 0.4rem;
  }

  p {
    color: $on-dark-body;
    flex: 1;
    font-size: 0.92rem;
    line-height: 1.5;
    margin-bottom: 0;
  }
}
</style>

<script>
import { useI18n } from 'vue-i18n';
import SectionTitle from '@/Components/SectionTitle.vue';

export default {
  components: {
    SectionTitle
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'A choice that makes the difference.': 'A choice that makes the difference.',
          'Design, code, hosting, search and ads sit with us. You do not juggle five vendors for every skill.': 'Design, code, hosting, search and ads sit with us. You do not juggle five vendors for every skill.',
          'Extras stay on the plan': 'Extras stay on the plan',
          'Hosting, domain, SSL and email ship with the build. They stay on your plan, not as later add-ons.': 'Hosting, domain, SSL and email ship with the build. They stay on your plan, not as later add-ons.',
          'Lowest price guaranteed': 'Lowest price guaranteed',
          'Lowest price on real work': 'Lowest price on real work',
          'Never get deceived by company histories, fake testimonials or free gifts. What matters is how we work, what we charge, who looks after the site.': 'Never get deceived by company histories, fake testimonials or free gifts. What matters is how we work, what we charge, who looks after the site.',
          'One team for the whole job': 'One team for the whole job',
          'Why choose us?': 'Why choose us?',
          'You pay for the website, not a high-rise studio. Custom work on a plan at the lowest price.': 'You pay for the website, not a high-rise studio. Custom work on a plan at the lowest price.'
        },
        'zh-MY': {
          'A choice that makes the difference.': '您至关重要的抉择。',
          'Design, code, hosting, search and ads sit with us. You do not juggle five vendors for every skill.': '设计、开发、托管、搜索广告全由我们做。不必同时找五家供应商。',
          'Extras stay on the plan': '配套含托管和域名',
          'Hosting, domain, SSL and email ship with the build. They stay on your plan, not as later add-ons.': '托管、域名、SSL和邮箱都包了。一直留在配套里，事后不加价。',
          'Lowest price guaranteed': '最低价保证',
          'Lowest price on real work': '最低价做定制网站',
          'Never get deceived by company histories, fake testimonials or free gifts. What matters is how we work, what we charge, who looks after the site.': '别被漂亮的公司历史、虚假的客户推荐或无用的赠品迷惑。真正重要的是这些：我们如何做事、如何收费，以及网站上线之后由谁负责照顾，这才是重点。',
          'One team for the whole job': '全套工作同一团队',
          'Why choose us?': '为何选择我们?',
          'You pay for the website, not a high-rise studio. Custom work on a plan at the lowest price.': '钱不花在高档办公室，全用在网站。按配套做定制，价格保证最低。'
        },
        'ms-MY': {
          'A choice that makes the difference.': 'Pilihan anda yang menimbulkan perbezaan.',
          'Design, code, hosting, search and ads sit with us. You do not juggle five vendors for every skill.': 'Reka bentuk, kod, hosting, carian dan iklan diurus satu pasukan. Tak perlu cari lima vendor lain untuk setiap kerja.',
          'Extras stay on the plan': 'Tambahan kekal dalam pelan',
          'Hosting, domain, SSL and email ship with the build. They stay on your plan, not as later add-ons.': 'Hosting, domain, SSL dan emel datang bersama laman web. Ia kekal dalam pelan, bukan caj tambahan.',
          'Lowest price guaranteed': 'Jaminan harga terendah',
          'Lowest price on real work': 'Kerja sebenar, harga terendah',
          'Never get deceived by company histories, fake testimonials or free gifts. What matters is how we work, what we charge, who looks after the site.': 'Jangan tertipu dengan sejarah syarikat yang mewah, testimoni palsu atau barangan percuma tidak berguna. Inilah yang benar-benar penting kepada anda.',
          'One team for the whole job': 'Satu pasukan urus semua',
          'Why choose us?': 'Kenapa pilih kami?',
          'You pay for the website, not a high-rise studio. Custom work on a plan at the lowest price.': 'Bayar untuk laman web, bukan kos pejabat mewah. Kerja tersuai dalam pelan pada harga paling rendah.'
        }
      }
    });

    return { t };
  }
};
</script>
